"use client";

import {
	type ComponentType,
	type FormEvent,
	type Key,
	type ReactNode,
	useEffect,
	useState
} from "react";

import {
	Button,
	Heading,
	InputText,
	Link,
	Notification,
	RadioGroup,
	Select,
	SelectItem,
	Sheets,
	Text
} from "@pamperedchef/design-system";
import { isPhoneNumber, isPostalCode } from "@/domain/address";
import { phoneDigits } from "@/composites/foundation/utilities/phone";

import {
	EMPTY_SHIPPING_ADDRESS,
	ShippingAddressFields,
	type ShippingAddressErrors,
	type ShippingAddressValues
} from "./ShippingAddressFields";
import * as styles from "./CreateListPanel.module.scss";

export type CreateListPanelKind = "wishlist" | "registry";

export type CreateListPanelStatus = "idle" | "submitting" | "success" | "error";

export type CreateListPanelProductError = "out-of-stock" | "not-found";

export type CreateListPanelCountry = "US" | "CA";

export interface CreateListPanelOccasion {
	code: string;
	label: string;
}

export interface CreateListPanelShippingAddress {
	firstName: string;
	lastName: string;
	phone: string;
	line1: string;
	line2?: string;
	town: string;
	stateCode: string;
	postalCode: string;
}

export type CreateListPanelListFor =
	| { mode: "me" }
	| {
			mode: "someoneElse";
			firstName: string;
			lastName: string;
			email: string;
	  };

export interface CreateListPanelValues {
	name: string;
	occasion: string;
	eventDate?: string;
	isPrivate: boolean;
	listFor: CreateListPanelListFor;
	shippingAddress?: CreateListPanelShippingAddress;
}

export interface CreateListPanelConsultant {
	name: string;
	location?: string;
}

export interface CreateListPanelCreatedList {
	name: string;
	productAdded: boolean;
	productError?: CreateListPanelProductError;
}

export interface CreateListPanelProps {
	kind: CreateListPanelKind;
	isOpen: boolean;
	onOpenChange: (next: boolean) => void;
	onSubmit: (values: CreateListPanelValues) => void;
	status: CreateListPanelStatus;
	occasions: readonly CreateListPanelOccasion[];
	country: CreateListPanelCountry;
	consultant?: CreateListPanelConsultant;
	onFindConsultant: () => void;
	onRemoveConsultant: () => void;
	errorMessage?: string;
	createdList?: CreateListPanelCreatedList;
	viewListHref?: string;
	canCreateForOthers?: boolean;
	children?: ReactNode;
}

type Visibility = "" | "public" | "private";

type AddShippingInfo = "" | "yes" | "no";

type ListFor = "me" | "someoneElse";

interface RecipientValues {
	firstName: string;
	lastName: string;
	email: string;
}

interface FormValues {
	name: string;
	occasion: string;
	eventDate: string;
	listFor: ListFor;
	recipient: RecipientValues;
	visibility: Visibility;
	addShippingInfo: AddShippingInfo;
	address: ShippingAddressValues;
}

type FieldErrors = Partial<
	Record<
		| "name"
		| "occasion"
		| "eventDate"
		| "visibility"
		| "addShippingInfo"
		| "recipientFirstName"
		| "recipientLastName"
		| "recipientEmail",
		string
	>
>;

const PERSON_NAME = /^[A-Za-z][A-Za-z' -]*$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OccasionItem = SelectItem as unknown as ComponentType<{
	key?: Key;
	label?: ReactNode;
	textValue?: string;
	children?: ReactNode;
}>;

const NAME_MAX_LENGTH = 100;

const COPY = {
	wishlist: {
		title: "Create a Wish List",
		nameLabel: "List Name",
		listForLabel: "List For",
		submit: "Create Wish List",
		successTitle: "Your wish list is ready",
		noun: "wish list"
	},
	registry: {
		title: "Create a Registry",
		nameLabel: "Registry Name",
		listForLabel: "Registry For",
		submit: "Create Registry",
		successTitle: "Your registry is ready",
		noun: "registry"
	}
} as const;

const NAME_PLACEHOLDER = "Ex. Friend's Wedding";

const EMPTY_RECIPIENT: RecipientValues = {
	firstName: "",
	lastName: "",
	email: ""
};

const EMPTY_VALUES: FormValues = {
	name: "",
	occasion: "",
	eventDate: "",
	listFor: "me",
	recipient: EMPTY_RECIPIENT,
	visibility: "",
	addShippingInfo: "",
	address: EMPTY_SHIPPING_ADDRESS
};

const RECIPIENT_ERROR_KEYS: Record<keyof RecipientValues, keyof FieldErrors> = {
	firstName: "recipientFirstName",
	lastName: "recipientLastName",
	email: "recipientEmail"
};

export function CreateListPanel({
	kind,
	isOpen,
	onOpenChange,
	onSubmit,
	status,
	occasions,
	country,
	consultant,
	onFindConsultant,
	onRemoveConsultant,
	errorMessage,
	createdList,
	viewListHref,
	canCreateForOthers = false,
	children
}: CreateListPanelProps) {
	const copy = COPY[kind];
	const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const [addressErrors, setAddressErrors] = useState<ShippingAddressErrors>(
		{}
	);

	useEffect(() => {
		if (isOpen) {
			setValues(EMPTY_VALUES);
			setFieldErrors({});
			setAddressErrors({});
		}
	}, [isOpen, kind]);

	const submitting = status === "submitting";
	const succeeded = status === "success" && createdList !== undefined;
	const needsAddress = requiresAddress(values);

	const close = () => onOpenChange(false);

	const forSomeoneElse =
		canCreateForOthers && values.listFor === "someoneElse";

	const update = (
		patch: Partial<Omit<FormValues, "address" | "recipient">>
	) => {
		setValues((current) => ({ ...current, ...patch }));
		setFieldErrors((current) => {
			const next = { ...current };
			for (const key of Object.keys(patch) as (keyof FieldErrors)[]) {
				delete next[key];
			}
			return next;
		});
	};

	const updateRecipient = (patch: Partial<RecipientValues>) => {
		setValues((current) => ({
			...current,
			recipient: { ...current.recipient, ...patch }
		}));
		setFieldErrors((current) => {
			const next = { ...current };
			for (const key of Object.keys(patch) as (keyof RecipientValues)[]) {
				delete next[RECIPIENT_ERROR_KEYS[key]];
			}
			return next;
		});
	};

	const updateAddress = (patch: Partial<ShippingAddressValues>) => {
		setValues((current) => ({
			...current,
			address: { ...current.address, ...patch }
		}));
		setAddressErrors((current) => {
			const next = { ...current };
			for (const key of Object.keys(
				patch
			) as (keyof ShippingAddressValues)[]) {
				delete next[key];
			}
			return next;
		});
	};

	const submit = () => {
		if (submitting) {
			return;
		}
		const errors = validate(
			kind,
			values,
			country,
			copy.noun,
			forSomeoneElse
		);
		setFieldErrors(errors.fields);
		setAddressErrors(errors.address);
		if (
			Object.keys(errors.fields).length > 0 ||
			Object.keys(errors.address).length > 0
		) {
			return;
		}
		onSubmit({
			name: values.name.trim(),
			occasion: values.occasion,
			eventDate: values.eventDate || undefined,
			isPrivate: values.visibility === "private",
			listFor: forSomeoneElse
				? {
						mode: "someoneElse",
						firstName: values.recipient.firstName.trim(),
						lastName: values.recipient.lastName.trim(),
						email: values.recipient.email.trim()
					}
				: { mode: "me" },
			...(needsAddress
				? { shippingAddress: toShippingAddress(values.address) }
				: {})
		});
	};

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();
		submit();
	};

	return (
		<Sheets
			className={styles["sheet"]}
			title={succeeded ? copy.successTitle : copy.title}
			closeButtonLabel="Close"
			mobileVariant="fullscreen"
			isOpen={isOpen}
			isDismissable={!submitting}
			onOpenChange={onOpenChange}
			onClosePress={close}
			primaryButton={
				succeeded ? (
					<Button onPress={close}>Continue Shopping</Button>
				) : (
					<Button
						onPress={submit}
						isDisabled={submitting}
						data-loading={submitting}
					>
						{copy.submit}
					</Button>
				)
			}
			secondaryButton={
				succeeded ? undefined : (
					<Button onPress={close} isDisabled={submitting}>
						Cancel
					</Button>
				)
			}
		>
			<div data-component="CreateListPanel" data-kind={kind}>
				{succeeded ? (
					<SuccessView
						createdList={createdList}
						noun={copy.noun}
						viewListHref={viewListHref}
					/>
				) : (
					<form
						className={styles["form"]}
						onSubmit={handleFormSubmit}
						noValidate
					>
						{status === "error" && errorMessage ? (
							<Notification
								kind="error"
								content={errorMessage}
								cta={{ link: "", title: "" }}
							/>
						) : null}

						<SectionLabel>Event Info</SectionLabel>

						<div className={styles["field"]}>
							<Select
								label="Occasion"
								name="occasion"
								isRequired
								placeholderText="Select occasion"
								selectedKey={values.occasion || null}
								onSelectionChange={(key) =>
									update({ occasion: String(key ?? "") })
								}
								errorMessage={fieldErrors.occasion}
								isDisabled={submitting}
							>
								{occasions.map((occasion) => (
									<OccasionItem
										key={occasion.code}
										label={occasion.label}
										textValue={occasion.label}
									>
										{occasion.label}
									</OccasionItem>
								))}
							</Select>
						</div>

						<InputText
							label="Event Date"
							name="eventDate"
							type="date"
							isRequired={kind === "registry"}
							value={values.eventDate}
							onChange={(eventDate) => update({ eventDate })}
							isInvalid={!!fieldErrors.eventDate}
							errorMessage={fieldErrors.eventDate}
							isDisabled={submitting}
						/>

						{canCreateForOthers ? (
							<RadioGroup
								label={copy.listForLabel}
								name="listFor"
								inline
								value={values.listFor}
								onChange={(value) =>
									update({ listFor: value as ListFor })
								}
								isDisabled={submitting}
								items={[
									{ value: "me", children: "Me" },
									{
										value: "someoneElse",
										children: "Someone else"
									}
								]}
							/>
						) : null}

						{forSomeoneElse ? (
							<>
								<div className={styles["row"]}>
									<InputText
										label="First Name"
										name="recipientFirstName"
										autoComplete="off"
										isRequired
										maxLength={25}
										value={values.recipient.firstName}
										onChange={(firstName) =>
											updateRecipient({ firstName })
										}
										isInvalid={
											!!fieldErrors.recipientFirstName
										}
										errorMessage={
											fieldErrors.recipientFirstName
										}
										isDisabled={submitting}
									/>
									<InputText
										label="Last Name"
										name="recipientLastName"
										autoComplete="off"
										isRequired
										maxLength={25}
										value={values.recipient.lastName}
										onChange={(lastName) =>
											updateRecipient({ lastName })
										}
										isInvalid={
											!!fieldErrors.recipientLastName
										}
										errorMessage={
											fieldErrors.recipientLastName
										}
										isDisabled={submitting}
									/>
								</div>
								<InputText
									label="Email"
									name="recipientEmail"
									type="email"
									inputMode="email"
									autoComplete="off"
									isRequired
									maxLength={255}
									value={values.recipient.email}
									onChange={(email) =>
										updateRecipient({ email })
									}
									isInvalid={!!fieldErrors.recipientEmail}
									errorMessage={fieldErrors.recipientEmail}
									isDisabled={submitting}
								/>
							</>
						) : null}

						<InputText
							label={copy.nameLabel}
							name="name"
							isRequired={!forSomeoneElse}
							maxLength={NAME_MAX_LENGTH}
							placeholder={NAME_PLACEHOLDER}
							value={values.name}
							onChange={(name) => update({ name })}
							isInvalid={!!fieldErrors.name}
							errorMessage={fieldErrors.name}
							isDisabled={submitting}
						/>

						<ConsultantRow
							consultant={consultant}
							noun={copy.noun}
							isDisabled={submitting}
							onFind={onFindConsultant}
							onRemove={onRemoveConsultant}
						/>

						<RadioGroup
							label="Visibility on Search Results"
							name="visibility"
							isRequired
							value={values.visibility || null}
							onChange={(value) =>
								update({
									visibility: value as Visibility,
									addShippingInfo: ""
								})
							}
							errorMessage={fieldErrors.visibility}
							isDisabled={submitting}
							items={[
								{ value: "public", children: "Public" },
								{ value: "private", children: "Private" }
							]}
						/>

						<SectionLabel>Shipping Info</SectionLabel>

						{values.visibility === "private" ? (
							<RadioGroup
								label="Add shipping info?"
								name="addShippingInfo"
								inline
								isRequired
								value={values.addShippingInfo || null}
								onChange={(value) =>
									update({
										addShippingInfo:
											value as AddShippingInfo
									})
								}
								errorMessage={fieldErrors.addShippingInfo}
								isDisabled={submitting}
								items={[
									{ value: "yes", children: "Yes" },
									{ value: "no", children: "No" }
								]}
							/>
						) : null}

						{needsAddress ? (
							<ShippingAddressFields
								country={country}
								values={values.address}
								errors={addressErrors}
								onChange={updateAddress}
								isDisabled={submitting}
							/>
						) : null}

						<button type="submit" hidden aria-hidden="true" />
					</form>
				)}
				{children}
			</div>
		</Sheets>
	);
}

function SectionLabel({ children }: { children: ReactNode }) {
	return (
		<Text tag="p" styledAs="xsb" className={styles["section-label"]}>
			{children}
		</Text>
	);
}

function ConsultantRow({
	consultant,
	noun,
	isDisabled,
	onFind,
	onRemove
}: {
	consultant?: CreateListPanelConsultant;
	noun: string;
	isDisabled: boolean;
	onFind: () => void;
	onRemove: () => void;
}) {
	return (
		<div
			className={styles["consultant"]}
			data-testid="create-list-consultant"
		>
			<Text tag="p" styledAs="smr">
				Your Consultant:
			</Text>
			{consultant ? (
				<>
					<Text tag="p" styledAs="smb">
						{consultant.location
							? `${consultant.name}, ${consultant.location}`
							: consultant.name}
					</Text>
					<div className={styles["consultant__actions"]}>
						<Button
							kind="tertiary"
							size="sm"
							onPress={onFind}
							isDisabled={isDisabled}
						>
							Change
						</Button>
						<Button
							kind="tertiary"
							size="sm"
							onPress={onRemove}
							isDisabled={isDisabled}
						>
							Remove
						</Button>
					</div>
				</>
			) : (
				<>
					<Text tag="p" styledAs="smr">
						Connect a consultant who can help with your {noun}.
					</Text>
					<div className={styles["consultant__actions"]}>
						<Button
							kind="tertiary"
							size="sm"
							onPress={onFind}
							isDisabled={isDisabled}
						>
							Find a Consultant
						</Button>
					</div>
				</>
			)}
		</div>
	);
}

function SuccessView({
	createdList,
	noun,
	viewListHref
}: {
	createdList: CreateListPanelCreatedList;
	noun: string;
	viewListHref?: string;
}) {
	return (
		<div className={styles["success"]} role="status">
			<Heading tag="h3">{createdList.name}</Heading>
			<Text tag="p" styledAs="mdr">
				Your {noun} has been created. {productMessage(createdList)}
			</Text>
			{viewListHref ? (
				<Link
					href={viewListHref}
					size="md"
					className={styles["success__link"]}
				>
					View {noun}
				</Link>
			) : null}
		</div>
	);
}

function productMessage(createdList: CreateListPanelCreatedList): string {
	if (createdList.productAdded) {
		return "This item has been added to it.";
	}
	switch (createdList.productError) {
		case "out-of-stock":
			return "This item is out of stock, so it was not added.";
		case "not-found":
			return "This item could not be added.";
		default:
			return "";
	}
}

function requiresAddress(values: FormValues): boolean {
	return (
		values.visibility === "public" ||
		(values.visibility === "private" && values.addShippingInfo === "yes")
	);
}

function toShippingAddress(
	address: ShippingAddressValues
): CreateListPanelShippingAddress {
	const line2 = address.line2.trim();
	return {
		firstName: address.firstName.trim(),
		lastName: address.lastName.trim(),
		phone: phoneDigits(address.phone),
		line1: address.line1.trim(),
		...(line2 ? { line2 } : {}),
		town: address.town.trim(),
		stateCode: address.stateCode,
		postalCode: address.postalCode.trim().toUpperCase()
	};
}

function validate(
	kind: CreateListPanelKind,
	values: FormValues,
	country: CreateListPanelCountry,
	noun: string,
	forSomeoneElse: boolean
): { fields: FieldErrors; address: ShippingAddressErrors } {
	const fields: FieldErrors = {};
	if (!values.occasion) {
		fields.occasion = "Select an occasion.";
	}
	if (kind === "registry" && !values.eventDate) {
		fields.eventDate = "Enter the date of your event.";
	}
	if (!forSomeoneElse && !values.name.trim()) {
		fields.name = `Enter a name for your ${noun}.`;
	}
	if (forSomeoneElse) {
		Object.assign(fields, validateRecipient(values.recipient));
	}
	if (!values.visibility) {
		fields.visibility = `Choose who can see your ${noun}.`;
	}
	if (values.visibility === "private" && !values.addShippingInfo) {
		fields.addShippingInfo = "Choose whether to add shipping info.";
	}
	const address = requiresAddress(values)
		? validateAddress(values.address, country)
		: {};
	return { fields, address };
}

function validateRecipient(recipient: RecipientValues): FieldErrors {
	const errors: FieldErrors = {};
	const firstName = recipient.firstName.trim();
	const lastName = recipient.lastName.trim();
	const email = recipient.email.trim();
	if (!firstName) {
		errors.recipientFirstName = "Enter their first name.";
	} else if (!PERSON_NAME.test(firstName)) {
		errors.recipientFirstName = "Use letters only in the first name.";
	}
	if (!lastName) {
		errors.recipientLastName = "Enter their last name.";
	} else if (!PERSON_NAME.test(lastName)) {
		errors.recipientLastName = "Use letters only in the last name.";
	}
	if (!email) {
		errors.recipientEmail = "Enter their email address.";
	} else if (!EMAIL.test(email)) {
		errors.recipientEmail = "Enter a valid email address.";
	}
	return errors;
}

function validateAddress(
	address: ShippingAddressValues,
	country: CreateListPanelCountry
): ShippingAddressErrors {
	const errors: ShippingAddressErrors = {};
	if (!address.firstName.trim()) {
		errors.firstName = "Enter a first name.";
	}
	if (!address.lastName.trim()) {
		errors.lastName = "Enter a last name.";
	}
	if (!isPhoneNumber(address.phone)) {
		errors.phone = "Enter a 10-digit phone number.";
	}
	if (!address.line1.trim()) {
		errors.line1 = "Enter a street address.";
	}
	if (!address.town.trim()) {
		errors.town = "Enter a city.";
	}
	if (!address.stateCode) {
		errors.stateCode =
			country === "CA" ? "Select a province." : "Select a state.";
	}
	if (!isPostalCode(country, address.postalCode)) {
		errors.postalCode =
			country === "CA"
				? "Enter a valid postal code."
				: "Enter a valid zip code.";
	}
	return errors;
}
